import type { TemplateStepPreset } from "@/domains/templates/mutations";
import type { InputFieldDefinition, Variable } from "@/domains/templates/types";

type CatalogVariable = Pick<
  Variable,
  "variable_id" | "variable_name" | "variable_name_frontend"
> & {
  source_variable_id?: string | null;
};

type CreateReportFieldSources = {
  inputFields: InputFieldDefinition[];
  scopedInputFields: InputFieldDefinition[];
  steps: TemplateStepPreset[];
  catalogVariables: CatalogVariable[];
};

type ResolvedCreateReportFields = Pick<
  CreateReportFieldSources,
  "inputFields" | "scopedInputFields" | "steps"
>;

type VariableNameSource = {
  id?: string;
  sourceId?: string | null;
  name: string;
};

function inputFieldSource(field: InputFieldDefinition): VariableNameSource {
  return {
    id: field.variableId,
    sourceId: field.sourceVariableId,
    name: field.variableNameFrontend ?? field.key,
  };
}

function catalogVariableSource(variable: CatalogVariable): VariableNameSource {
  return {
    id: variable.variable_id,
    sourceId: variable.source_variable_id,
    name: variable.variable_name_frontend ?? variable.variable_name,
  };
}

export function resolveCreateReportFieldNames(
  params: CreateReportFieldSources,
): ResolvedCreateReportFields {
  const stepScopedFields = params.steps.flatMap(
    (step) => step.scopedInputFields ?? step.inputFields,
  );
  const sources = [
    ...params.scopedInputFields.map(inputFieldSource),
    ...stepScopedFields.map(inputFieldSource),
    ...params.catalogVariables.map(catalogVariableSource),
  ];
  const sourcesById = new Map(
    sources.flatMap((source) =>
      source.id ? ([[source.id, source]] as const) : [],
    ),
  );

  const resolveField = (field: InputFieldDefinition): InputFieldDefinition => {
    const visited = new Set<string>();
    let current = inputFieldSource(field);

    while (current.sourceId) {
      if (current.id) visited.add(current.id);
      const source = sourcesById.get(current.sourceId);
      if (!source || (source.id && visited.has(source.id))) break;
      current = source;
    }

    return {
      ...field,
      variableNameFrontend: current.name,
    };
  };

  return {
    inputFields: params.inputFields.map(resolveField),
    scopedInputFields: params.scopedInputFields.map(resolveField),
    steps: params.steps.map((step) => ({
      ...step,
      inputFields: step.inputFields.map(resolveField),
      scopedInputFields: step.scopedInputFields?.map(resolveField),
    })),
  };
}
